/**
 * 卡片管理页面
 * 包含：卡片冻结/解冻、限额调整、卡片注销
 */

import { useCallback } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import Ripple from '@/components/Ripple'

// 管理项类型
interface ManageItem {
  icon: string
  title: string
  action: () => void
}

// 卡片信息接口
interface CardInfo {
  maskedPan?: string
  balance?: number
  backgroundImage?: string
  cardIssued?: boolean
  status?: string
}

export default function CardManage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const cardId = searchParams.get('id') || ''
  const cardNo = searchParams.get('cardNo') || ''
  const isFrozen = searchParams.get('frozen') === 'true'

  // 从路由 state 获取卡片信息
  const cardInfo: CardInfo = location.state || {}

  // 判断卡片是否为 active 状态
  const isActive = cardInfo.status === 'active'

  // 跳转到冻结/解冻页面
  const handleFreeze = useCallback(() => {
    navigate(`/card-v2/card-freeze?id=${cardId}&cardNo=${cardNo}&frozen=${isFrozen}`)
  }, [navigate, cardId, cardNo, isFrozen])

  // 跳转到限额调整页面
  const handleLimit = useCallback(() => {
    navigate(`/card-v2/card-limit?id=${cardId}`, {
      state: cardInfo
    })
  }, [navigate, cardId, cardInfo])

  // 跳转到卡片注销页面
  const handleCancel = useCallback(() => {
    navigate(`/card-v2/card-cancel?id=${cardId}&cardNo=${cardNo}`, {
      state: cardInfo
    })
  }, [navigate, cardId, cardNo, cardInfo])

  // 管理项列表（非 active 状态下不显示限额调整和卡片注销）
  const manageItems: ManageItem[] = [
    {
      icon: '/images/card-v2/manage-freeze-icon.svg',
      title: isFrozen ? t('卡片解冻') : t('卡片冻结'),
      action: handleFreeze
    },
    // 只有 active 状态才显示限额调整和卡片注销
    ...(isActive ? [
      {
        icon: '/images/card-v2/manage-limit-icon.svg',
        title: t('限额调整'),
        action: handleLimit
      },
      {
        icon: '/images/card-v2/manage-cancel-icon.svg',
        title: t('卡片注销'),
        action: handleCancel
      }
    ] : [])
  ]

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('卡管理')}</span>}
      />

      <div className="bg-white flex-1 pt-2">
        {manageItems.map((item, index) => (
          <Ripple key={index} className="mx-0">
            <div
              className="flex items-center justify-between px-5 py-4 cursor-pointer"
              onClick={item.action}
            >
              <div className="flex items-center">
                {/* 图标 */}
                <div className="w-12 h-12 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                  <img src={item.icon} alt="" className="w-6 h-6" />
                </div>
                {/* 标题 */}
                <span className="ml-4 text-base text-[#1D2129]">{item.title}</span>
              </div>
              {/* 箭头 */}
              <img src="/icon/right-info.svg" alt="" />
            </div>
          </Ripple>
        ))}
      </div>
    </PageLayout>
  )
}
